import React, { useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, TrendingUp, Coins, Landmark, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { currency } from "../lib/format";
import { toast } from "sonner";

const TYPES = [
    { key: "stock", label: "Appreciated Stock", icon: TrendingUp, body: "Donate publicly traded stock and skip capital gains tax." },
    { key: "daf", label: "Donor-Advised Fund", icon: Landmark, body: "Recommend a grant from Fidelity Charitable, Schwab, or Vanguard DAF." },
    { key: "crypto", label: "Cryptocurrency", icon: Coins, body: "BTC, ETH, and USDC accepted via our nonprofit crypto partner." },
    { key: "wire", label: "Wire Transfer", icon: Building2, body: "Direct wire from your corporate treasury or foundation account." },
];

export default function CorporatePage() {
    const [type, setType] = useState("stock");
    const [form, setForm] = useState({
        company_name: "", contact_name: "", contact_email: "", contact_phone: "",
        ticker: "", shares: "", estimated_value_usd: "",
        broker_name: "", dtc_number: "", designated_program: "",
        message: "",
    });
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    async function submit() {
        if (!form.company_name || !form.contact_name || !form.contact_email) {
            return toast.error("Company, contact name, and email are required");
        }
        setBusy(true);
        try {
            const payload = {
                company_name: form.company_name,
                contact_name: form.contact_name,
                contact_email: form.contact_email,
                contact_phone: form.contact_phone || null,
                donation_type: type,
                ticker: form.ticker || null,
                shares: form.shares ? parseFloat(form.shares) : null,
                estimated_value_usd: form.estimated_value_usd ? parseFloat(form.estimated_value_usd) : null,
                broker_name: form.broker_name || null,
                dtc_number: form.dtc_number || null,
                designated_program: form.designated_program || null,
                message: form.message || null,
            };
            const { data } = await api.post("/donations/stock", payload);
            setResult(data);
            toast.success("Pledge submitted — coordinator will follow up within one business day.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit");
        }
        setBusy(false);
    }

    if (result) {
        return (
            <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16" data-testid="stock-donation-success">
                <div className="card-warm rounded-2xl p-10">
                    <CheckCircle2 className="w-12 h-12 text-[var(--rl-success)]" />
                    <h1 className="font-serif text-4xl mt-4">Thank you.</h1>
                    <p className="text-[var(--rl-text-2)] mt-2">Confirmation ID <span className="font-mono uppercase">{result.id.slice(0, 8)}</span> · A rescue coordinator will contact you within one business day.</p>

                    <div className="mt-8 grid gap-3 rounded-xl bg-[var(--rl-cream)] p-5 border border-[var(--rl-border)]">
                        <div className="eyebrow">Transfer instructions</div>
                        <div className="grid sm:grid-cols-2 gap-3 text-sm">
                            <div><div className="text-[var(--rl-text-3)] text-xs uppercase tracking-widest">Receiving broker</div><div className="font-semibold">{result.transfer_instructions.receiving_broker}</div></div>
                            <div><div className="text-[var(--rl-text-3)] text-xs uppercase tracking-widest">Account name</div><div className="font-semibold">{result.transfer_instructions.account_name}</div></div>
                            <div><div className="text-[var(--rl-text-3)] text-xs uppercase tracking-widest">DTC number</div><div className="font-semibold font-mono">{result.transfer_instructions.dtc_number}</div></div>
                            <div><div className="text-[var(--rl-text-3)] text-xs uppercase tracking-widest">Account number</div><div className="font-semibold font-mono">{result.transfer_instructions.account_number}</div></div>
                        </div>
                        <p className="text-xs text-[var(--rl-text-2)] mt-2">{result.transfer_instructions.note}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="corporate-page">
            <div className="bg-[var(--rl-cream)] border-b border-[var(--rl-border)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                    <div className="eyebrow mb-3 inline-flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Corporate giving</div>
                    <h1 className="font-serif text-4xl lg:text-6xl tracking-tight max-w-4xl">Give stock, not cash.<br />Save dogs, not taxes.</h1>
                    <p className="mt-4 text-[var(--rl-text-2)] max-w-2xl">Donating appreciated securities to RescueLink Global Fund lets your corporation avoid capital gains and take a full fair-market-value deduction. We accept stock, DAF grants, cryptocurrency, and wire transfers.</p>
                    <div className="mt-6 flex flex-wrap gap-3 text-xs">
                        <span className="inline-flex items-center gap-1.5 bg-white/70 border border-[var(--rl-border)] px-3 py-1.5 rounded-full"><ShieldCheck className="w-3.5 h-3.5 text-[var(--rl-success)]" /> 501(c)(3) EIN 87-2214981</span>
                        <span className="inline-flex items-center gap-1.5 bg-white/70 border border-[var(--rl-border)] px-3 py-1.5 rounded-full">Charity Navigator ★★★★</span>
                        <span className="inline-flex items-center gap-1.5 bg-white/70 border border-[var(--rl-border)] px-3 py-1.5 rounded-full">$3.2M raised in 2025</span>
                    </div>
                </div>
            </div>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-5">
                    <div className="eyebrow mb-3">Choose gift type</div>
                    <div className="space-y-3">
                        {TYPES.map((t) => {
                            const Icon = t.icon;
                            const active = type === t.key;
                            return (
                                <button key={t.key} onClick={() => setType(t.key)}
                                        data-testid={`type-${t.key}`}
                                        className={`w-full text-left p-4 rounded-xl border transition ${active ? "border-[var(--rl-primary)] bg-[#fff5f3]" : "border-[var(--rl-border)] hover:bg-[var(--rl-cream)]"}`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${active ? "bg-[var(--rl-primary)] text-white" : "bg-[var(--rl-cream)]"}`}><Icon className="w-4 h-4" /></div>
                                        <div className="flex-1">
                                            <div className="font-serif text-lg">{t.label}</div>
                                            <div className="text-xs text-[var(--rl-text-2)] mt-0.5">{t.body}</div>
                                        </div>
                                        {active && <ArrowRight className="w-4 h-4 text-[var(--rl-primary)] mt-2" />}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-8 rounded-xl bg-[var(--rl-cream)] border border-[var(--rl-border)] p-5 text-sm">
                        <div className="eyebrow mb-2">Why give stock?</div>
                        <ul className="space-y-1.5 text-[var(--rl-text-2)]">
                            <li>✓ Skip capital gains tax on the appreciation</li>
                            <li>✓ Deduct full fair market value on your corporate return</li>
                            <li>✓ Fund more rescue per dollar than after-tax cash</li>
                        </ul>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <div className="card-warm rounded-2xl p-8">
                        <div className="eyebrow mb-4">Your details</div>
                        <div className="grid gap-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Company name *" v={form.company_name} on={(v) => setForm({ ...form, company_name: v })} testid="stock-company" />
                                <Field label="Contact name *" v={form.contact_name} on={(v) => setForm({ ...form, contact_name: v })} testid="stock-contact-name" />
                                <Field label="Email *" v={form.contact_email} on={(v) => setForm({ ...form, contact_email: v })} type="email" testid="stock-email" />
                                <Field label="Phone" v={form.contact_phone} on={(v) => setForm({ ...form, contact_phone: v })} testid="stock-phone" />
                            </div>

                            {type === "stock" && (
                                <div className="grid sm:grid-cols-3 gap-4">
                                    <Field label="Ticker" v={form.ticker} on={(v) => setForm({ ...form, ticker: v.toUpperCase() })} placeholder="AAPL" testid="stock-ticker" />
                                    <Field label="Shares" v={form.shares} on={(v) => setForm({ ...form, shares: v })} type="number" testid="stock-shares" />
                                    <Field label="Est. value (USD)" v={form.estimated_value_usd} on={(v) => setForm({ ...form, estimated_value_usd: v })} type="number" testid="stock-value" />
                                </div>
                            )}
                            {type === "stock" && (
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Field label="Your broker" v={form.broker_name} on={(v) => setForm({ ...form, broker_name: v })} placeholder="Fidelity, Schwab…" testid="stock-broker" />
                                    <Field label="Broker DTC #" v={form.dtc_number} on={(v) => setForm({ ...form, dtc_number: v })} testid="stock-dtc" />
                                </div>
                            )}
                            {type === "daf" && (
                                <Field label="DAF provider" v={form.broker_name} on={(v) => setForm({ ...form, broker_name: v })} placeholder="Fidelity Charitable, Schwab Charitable, Vanguard…" testid="stock-daf" />
                            )}
                            {type === "crypto" && (
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Field label="Currency" v={form.ticker} on={(v) => setForm({ ...form, ticker: v.toUpperCase() })} placeholder="BTC, ETH, USDC" testid="stock-crypto-ticker" />
                                    <Field label="Est. value (USD)" v={form.estimated_value_usd} on={(v) => setForm({ ...form, estimated_value_usd: v })} type="number" testid="stock-crypto-value" />
                                </div>
                            )}

                            <div>
                                <label className="eyebrow">Designated program (optional)</label>
                                <Select value={form.designated_program} onValueChange={(v) => setForm({ ...form, designated_program: v === "none" ? "" : v })}>
                                    <SelectTrigger className="mt-2 border-[var(--rl-border)]" data-testid="stock-program"><SelectValue placeholder="Global Fund (default)" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Global Fund (default)</SelectItem>
                                        <SelectItem value="meat_trade">Asia Meat-Trade Rescue</SelectItem>
                                        <SelectItem value="transport">International Transport</SelectItem>
                                        <SelectItem value="medical">Medical Emergency Fund</SelectItem>
                                        <SelectItem value="senior">Senior Dog Program</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="eyebrow">Message (optional)</label>
                                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                                          placeholder="Special instructions, tax-year timing, or a note for our team."
                                          className="mt-2 border-[var(--rl-border)]" data-testid="stock-message" />
                            </div>

                            <Button onClick={submit} disabled={busy} className="btn-primary w-full rounded-full h-12" data-testid="stock-submit">
                                {busy ? "Submitting…" : `Pledge ${form.estimated_value_usd ? currency(parseFloat(form.estimated_value_usd)) : "gift"}`}
                            </Button>
                            <div className="text-xs text-[var(--rl-text-3)] text-center">Your submission creates a pledge — funds only move after you initiate the transfer via your broker.</div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Field({ label, v, on, type = "text", placeholder, testid }) {
    return (
        <div>
            <label className="eyebrow">{label}</label>
            <Input value={v} onChange={(e) => on(e.target.value)} type={type} placeholder={placeholder}
                   className="mt-1.5 border-[var(--rl-border)] h-11" data-testid={testid} />
        </div>
    );
}
